import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { loginUser, signupUser } from "../features/user/userSlice";
import ForgotPasswordModal from "../components/ForgotPasswordModal";
import TermsContent from "../components/TermsContent";
import styles from "./LoginPage.module.css";

// --- CONSTANTS FOR VALIDATION ---
const MAX_USERNAME_LENGTH = 100;
const MAX_PASSWORD_LENGTH = 100;
const MAX_BRAND_NAME_LENGTH = 50;
const MAX_FIRST_NAME_LENGTH = 30;
const MAX_LAST_NAME_LENGTH = 30;

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const PASSWORD_COMPLEXITY_REGEX =
  /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}/;
const PHONE_NUMBER_REGEX = /^[+]?[1-9][\d]{0,15}$/;

function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [tncAccepted, setTncAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  // Partner logos for carousel
  const partnerLogos = [
    { src: "images/partners/6.png", alt: "" },
    { src: "images/partners/7.png", alt: "" },
    { src: "images/partners/ace_blend.png", alt: "Ace Blend Logo" },
    { src: "images/partners/ecosys.png", alt: "Ecosys Logo" },
    { src: "images/partners/neeman's.png", alt: "Neeman's Logo" },
    { src: "images/partners/ppc.png", alt: "PPC Logo" },
    { src: "images/partners/tbc.png", alt: "TBC Logo" },
  ];

  const CAROUSEL_DUPLICATES = 3;
  const carouselLogos = Array.from(
    { length: CAROUSEL_DUPLICATES },
    () => partnerLogos,
  ).flat();

  const trackRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  // Per-field messages from the server, keyed by field name, shown under the
  // input they belong to.
  const [fieldErrors, setFieldErrors] = useState({});
  const [signupIssues, setSignupIssues] = useState([]);

  // Signup form fields
  const [email, setEmail] = useState("");
  const [brandName, setBrandName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // Derive signup mode directly from the route so switching /login <-> /signup
  // never renders a stale frame and there is no redundant window.location read.
  const isSignup = location.pathname === "/signup";
  const loading = useSelector((state) => state.user.loading);
  const backendError = useSelector((state) => state.user.error);

  // This useEffect hook listens for changes in the backendError state
  // and updates the local error state to be displayed on the UI.
  useEffect(() => {
    if (backendError) {
      setError(backendError);
    } else {
      setError("");
    }
  }, [backendError]);

  // The same component renders both /login and /signup, so clear any messages
  // left over from the other mode rather than showing them again on return.
  useEffect(() => {
    setError("");
    setFieldErrors({});
    setSignupIssues([]);
  }, [isSignup]);

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setIsTermsModalOpen(false);
        setIsForgotPasswordOpen(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const handleOpenTerms = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log("LoginPage: Opening Terms Modal");
    setIsTermsModalOpen(true);
  };

  // Drop a field's message as soon as it is edited, so the form does not keep
  // showing a complaint about something the person has already corrected.
  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSignupIssues((prev) => prev.filter((issue) => issue.field !== key));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    // Trim all inputs to handle leading/trailing spaces
    const trimmedEmail = email.trim();
    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();
    const trimmedBrandName = brandName.trim();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedPhoneNumber = phoneNumber.trim();

    if (isSignup) {
      // --- SIGNUP VALIDATION ---
      // Every problem is collected before reporting, so the person sees the
      // full list once instead of discovering one issue per attempt.
      const errors = {};

      const requireField = (key, value, label, example) => {
        if (!value) {
          errors[key] =
            `${label} is required and is currently empty.` +
            (example ? ` For example: ${example}.` : "");
          return false;
        }
        return true;
      };

      requireField("first_name", trimmedFirstName, "First Name", "Priya");
      requireField("last_name", trimmedLastName, "Last Name", "Sharma");
      const hasBrand = requireField("brand_name", trimmedBrandName, "Brand Name", "Aurelia Home");
      const hasEmail = requireField("email", trimmedEmail, "Work Email", "priya@yourbrand.com");
      const hasPhone = requireField("phone_number", trimmedPhoneNumber, "Phone Number", "9876543210");
      const hasPassword = requireField("password", trimmedPassword, "Password");
      requireField("confirm_password", trimmedConfirmPassword, "Confirm Password");

      if (hasEmail && !EMAIL_REGEX.test(trimmedEmail)) {
        let reason = "";
        if (!trimmedEmail.includes("@")) {
          reason = " but it is missing the '@' symbol";
        } else if ((trimmedEmail.match(/@/g) || []).length > 1) {
          reason = " but it contains more than one '@' symbol";
        } else if (!trimmedEmail.split("@")[1]?.includes(".")) {
          reason =
            " but the part after '@' is not a complete domain — it should look like 'company.com'";
        }
        errors.email = `Work Email is not a valid email address. You entered '${trimmedEmail}'${reason}. Please enter a complete address such as priya@yourbrand.com.`;
      }

      if (hasPassword && !PASSWORD_COMPLEXITY_REGEX.test(trimmedPassword)) {
        const problems = [];
        if (trimmedPassword.length < 8) {
          problems.push(
            `it is ${8 - trimmedPassword.length} character(s) too short (you entered ${trimmedPassword.length}, at least 8 are needed)`,
          );
        }
        if (!/[A-Z]/.test(trimmedPassword)) problems.push("it has no uppercase letter");
        if (!/[a-z]/.test(trimmedPassword)) problems.push("it has no lowercase letter");
        if (!/[0-9]/.test(trimmedPassword)) problems.push("it has no number");
        if (!/[^A-Za-z0-9]/.test(trimmedPassword)) {
          problems.push("it has no special character such as ! @ # or $");
        }
        errors.password =
          "Password must be at least 8 characters long and contain an uppercase letter, a lowercase letter, a number and a special character." +
          (problems.length ? ` Problem(s) found: ${problems.join("; and ")}.` : "");
      }

      if (
        hasPassword &&
        trimmedConfirmPassword &&
        trimmedPassword !== trimmedConfirmPassword
      ) {
        errors.confirm_password =
          "Confirm Password does not match the password you entered above. Retype the same password in both boxes.";
      }

      if (
        hasPhone &&
        !PHONE_NUMBER_REGEX.test(trimmedPhoneNumber.replace(/[\s\-()]/g, ""))
      ) {
        const digits = trimmedPhoneNumber.replace(/\D/g, "");
        let reason;
        if (digits.length === 12 && digits.startsWith("91")) {
          reason =
            "it looks like you included the '+91' country code — please remove it and enter only the 10-digit number";
        } else if (digits.length > 10) {
          reason = `it has ${digits.length - 10} digit(s) too many`;
        } else if (digits.length < 10) {
          reason = `it is ${10 - digits.length} digit(s) short`;
        } else {
          reason = "it contains characters that are not allowed in a phone number";
        }
        errors.phone_number = `Phone Number must be a 10-digit mobile number. You entered '${trimmedPhoneNumber}', which has ${digits.length} digit(s) once spaces and symbols are removed. Problem: ${reason}. Enter it without the country code, e.g. 9876543210.`;
      }

      const checkMaxLength = (key, value, label, max) => {
        if (value.length > max) {
          errors[key] = `${label} must be ${max} characters or fewer, but you entered ${value.length} — that is ${value.length - max} too many. Please shorten it.`;
        }
      };
      if (hasBrand) checkMaxLength("brand_name", trimmedBrandName, "Brand Name", MAX_BRAND_NAME_LENGTH);
      checkMaxLength("first_name", trimmedFirstName, "First Name", MAX_FIRST_NAME_LENGTH);
      checkMaxLength("last_name", trimmedLastName, "Last Name", MAX_LAST_NAME_LENGTH);

      if (!tncAccepted) {
        errors.tnc =
          "You need to accept the Terms & Conditions before you can create an account. Tick the box below to continue.";
      }

      if (Object.keys(errors).length > 0) {
        const count = Object.keys(errors).length;
        setFieldErrors(errors);
        setSignupIssues([]);
        setError(
          count === 1
            ? Object.values(errors)[0]
            : `${count} fields need your attention. Each one is explained below the field it applies to.`,
        );
        return;
      }

      setFieldErrors({});
      setSignupIssues([]);

      // If all validation passes, proceed with API call
      const signupData = {
        email: trimmedEmail,
        password: trimmedPassword,
        brand_name: trimmedBrandName,
        first_name: trimmedFirstName,
        last_name: trimmedLastName,
        phone_number: trimmedPhoneNumber,
      };

      try {
        const resultAction = await dispatch(signupUser(signupData));

        if (signupUser.fulfilled.match(resultAction)) {
          // Handle the auto-login response
          const responsePayload = resultAction.payload;

          setSuccessMessage(
            responsePayload.message || "Account created successfully!",
          );

          // Clear form on success
          setEmail("");
          setPassword("");
          setConfirmPassword("");
          setBrandName("");
          setFirstName("");
          setLastName("");
          setPhoneNumber("");

          // Wait a brief moment to show the success message
          setTimeout(() => {
            // Redirect based on is_new_user flag
            if (responsePayload.is_new_user === true) {
              // New users go to onboarding
              navigate("/onboarding");
            } else {
              // Existing users go to dashboard
              navigate("/growth-suite");
            }
          }, 1500);
        }
        if (signupUser.rejected.match(resultAction)) {
          const payload = resultAction.payload || {};
          // The server names each field it rejected and explains why, so show
          // its wording rather than replacing it with something vaguer.
          setError(
            payload.message ||
              resultAction.error?.message ||
              "Signup failed. Please try again."
          );
          setFieldErrors(payload.fieldErrors || {});
          setSignupIssues(payload.issues || []);
        }
      } catch (err) {
        // This block handles general network errors or unexpected issues
        setError(err.message || "An unexpected error occurred during signup.");
      }
    } else {
      // --- LOGIN VALIDATION ---
      if (!trimmedUsername) {
        setError("Username is required.");
        return;
      }
      if (!trimmedPassword) {
        setError("Password is required.");
        return;
      }

      if (
        trimmedUsername.length > MAX_USERNAME_LENGTH ||
        trimmedPassword.length > MAX_PASSWORD_LENGTH
      ) {
        setError("Username and password are too long.");
        return;
      }

      const resultAction = await dispatch(
        loginUser({ username: trimmedUsername, password: trimmedPassword }),
      );
      if (loginUser.fulfilled.match(resultAction)) {
        navigate("/growth-suite");
      } else if (loginUser.rejected.match(resultAction)) {
        const errorMessage = resultAction.payload || resultAction.error.message;
        setError(errorMessage);
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const resetForm = () => {
    setUsername("");
    setPassword("");
    setEmail("");
    setBrandName("");
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
    setConfirmPassword("");
    setTncAccepted(false);
    setError("");
    setSuccessMessage("");
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let animationId;
    let position = 0;
    let isPaused = false;
    const speed = 0.5; // pixels per frame
    let loopWidth = 0;

    const measure = () => {
      const fullWidth = track.scrollWidth;
      if (!fullWidth) return;
      loopWidth = fullWidth / CAROUSEL_DUPLICATES;
    };

    const animate = () => {
      if (!isPaused) {
        if (!loopWidth) {
          measure();
        }
        position -= speed;

        if (loopWidth && Math.abs(position) >= loopWidth) {
          position += loopWidth;
        }

        track.style.transform = `translateX(${position}px)`;
      }
      animationId = requestAnimationFrame(animate);
    };

    const handleMouseEnter = () => {
      isPaused = true;
    };

    const handleMouseLeave = () => {
      isPaused = false;
    };

    const carousel = track.parentElement;
    carousel.addEventListener("mouseenter", handleMouseEnter);
    carousel.addEventListener("mouseleave", handleMouseLeave);

    measure();
    const resizeObserver = new ResizeObserver(() => {
      measure();
    });
    resizeObserver.observe(track);

    animate();

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      resizeObserver.disconnect();
      carousel.removeEventListener("mouseenter", handleMouseEnter);
      carousel.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  const switchMode = () => {
    resetForm();

    navigate(isSignup ? "/login" : "/signup");
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleOpenForgotPassword = () => {
    console.log("LoginPage: opening ForgotPasswordModal");
    setIsForgotPasswordOpen(true);
  };

  return (
    <div className={styles.pageContainer}>
      {successMessage && (
        <div className={styles.successBanner}>
          <div className={styles.successContent}>
            <div className={styles.successIcon}>✓</div>
            <div className={styles.successText}>
              <div className={styles.successTitle}>
                🎉 Account Created Successfully! 🎉
              </div>
              <div className={styles.successMessage}>{successMessage}</div>
            </div>
            <button
              className={styles.successClose}
              onClick={() => setSuccessMessage("")}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <section
        className={`${styles.mainSection} ${isSignup ? styles.signupMainSection : ""}`}
      >
        <div className={styles.mainPageWrapper}>
          <main
            className={`${styles.loginContainer} ${isSignup ? styles.signupContainer : ""}`}
          >
            <div
              className={`${styles.loginPanel} ${isSignup ? styles.signupPanel : ""}`}
            >
              <img
                src="/images/Suspire_Logo.png"
                alt="Suspire Logo"
                className={`${styles.logo} ${isSignup ? styles.signupLogo : ""}`}
              />
              {!isSignup && (
                <header className={styles.loginHeader}>
                  <h1 className={styles.title}>
                    Welcome to <strong>Suspire Console</strong>
                  </h1>
                  <p className={styles.subtitle}>
                    Your marketing growth, order processing and AI tools - all
                    in one place.
                  </p>
                </header>
              )}

              <form
                className={`${styles.loginForm} ${isSignup ? styles.signupFormCompact : ""}`}
                onSubmit={handleSubmit}
              >
                {isSignup ? (
                  <>
                    <div className={styles.formGroup}>
                      <label htmlFor="email">
                        Email <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.inputWrapper}>
                        <input
                          type="email"
                          id="email"
                          placeholder="Enter your email..."
                          value={email}
                          onChange={(e) => { setEmail(e.target.value); clearFieldError('email'); }}
                          required
                        />
                      </div>
                      {fieldErrors.email && (
                        <span className={styles.fieldError}>{fieldErrors.email}</span>
                      )}
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="password">
                        Password <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.inputWrapper}>
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          placeholder="Enter your password..."
                          value={password}
                          onChange={(e) => { setPassword(e.target.value); clearFieldError('password'); }}
                          required
                        />
                        <button
                          type="button"
                          className={styles.toggleButton}
                          onClick={togglePasswordVisibility}
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                        >
                          <img
                            src={
                              showPassword
                                ? "/images/HidePassword.svg"
                                : "/images/ShowPassword.svg"
                            }
                            alt={
                              showPassword ? "Hide password" : "Show password"
                            }
                            className={styles.toggleIcon}
                          />
                        </button>
                      </div>
                      {fieldErrors.password && (
                        <span className={styles.fieldError}>{fieldErrors.password}</span>
                      )}
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="confirmPassword">
                        Confirm Password{" "}
                        <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.inputWrapper}>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          id="confirmPassword"
                          placeholder="Confirm your password..."
                          value={confirmPassword}
                          onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError('confirm_password'); }}
                          required
                        />
                        <button
                          type="button"
                          className={styles.toggleButton}
                          onClick={toggleConfirmPasswordVisibility}
                          aria-label={
                            showConfirmPassword
                              ? "Hide password"
                              : "Show password"
                          }
                        >
                          <img
                            src={
                              showConfirmPassword
                                ? "/images/HidePassword.svg"
                                : "/images/ShowPassword.svg"
                            }
                            alt={
                              showConfirmPassword
                                ? "Hide password"
                                : "Show password"
                            }
                            className={styles.toggleIcon}
                          />
                        </button>
                      </div>
                      {fieldErrors.confirm_password && (
                        <span className={styles.fieldError}>{fieldErrors.confirm_password}</span>
                      )}
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="brandName">
                        Brand Name <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.inputWrapper}>
                        <input
                          type="text"
                          id="brandName"
                          placeholder="Enter your brand name..."
                          value={brandName}
                          onChange={(e) => { setBrandName(e.target.value); clearFieldError('brand_name'); }}
                          required
                        />
                      </div>
                      {fieldErrors.brand_name && (
                        <span className={styles.fieldError}>{fieldErrors.brand_name}</span>
                      )}
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="firstName">
                        First Name <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.inputWrapper}>
                        <input
                          type="text"
                          id="firstName"
                          placeholder="Enter your first name..."
                          value={firstName}
                          onChange={(e) => { setFirstName(e.target.value); clearFieldError('first_name'); }}
                          required
                        />
                      </div>
                      {fieldErrors.first_name && (
                        <span className={styles.fieldError}>{fieldErrors.first_name}</span>
                      )}
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="lastName">
                        Last Name <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.inputWrapper}>
                        <input
                          type="text"
                          id="lastName"
                          placeholder="Enter your last name..."
                          value={lastName}
                          onChange={(e) => { setLastName(e.target.value); clearFieldError('last_name'); }}
                          required
                        />
                      </div>
                      {fieldErrors.last_name && (
                        <span className={styles.fieldError}>{fieldErrors.last_name}</span>
                      )}
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="phoneNumber">
                        Phone Number <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.inputWrapper}>
                        <input
                          type="tel"
                          id="phoneNumber"
                          placeholder="Enter your phone number..."
                          value={phoneNumber}
                          onChange={(e) => { setPhoneNumber(e.target.value); clearFieldError('phone_number'); }}
                          required
                        />
                      </div>
                      {fieldErrors.phone_number && (
                        <span className={styles.fieldError}>{fieldErrors.phone_number}</span>
                      )}
                    </div>

                    <div className={styles.tncRow}>
                      <div
                        className={styles.tncLabel}
                        style={{ cursor: "default" }}
                      >
                        <input
                          type="checkbox"
                          id="tnc-checkbox"
                          checked={tncAccepted}
                          onChange={(e) => { setTncAccepted(e.target.checked); clearFieldError('tnc'); }}
                          className={styles.tncCheckbox}
                        />
                        <span className={styles.tncText}>
                          <label
                            htmlFor="tnc-checkbox"
                            style={{ cursor: "pointer" }}
                          >
                            Suspire is a curated platform. Submitting this form
                            is the first step in being considered for
                            onboarding.
                          </label>{" "}
                          <button
                            type="button"
                            className={styles.tncLink}
                            onClick={handleOpenTerms}
                          >
                            View Terms
                          </button>
                        </span>
                      </div>
                      {fieldErrors.tnc && (
                        <span className={styles.fieldError}>{fieldErrors.tnc}</span>
                      )}
                    </div>
                  </>
                ) : (
                  // Login form
                  <>
                    <div className={styles.formGroup}>
                      <label htmlFor="username">
                        Username <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.inputWrapper}>
                        <input
                          type="text"
                          id="username"
                          placeholder="Enter your username..."
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="password">
                        Password <span className={styles.required}>*</span>
                      </label>
                      <div className={styles.inputWrapper}>
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          placeholder="Enter your password..."
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          className={styles.toggleButton}
                          onClick={togglePasswordVisibility}
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                        >
                          <img
                            src={
                              showPassword
                                ? "/images/HidePassword.svg"
                                : "/images/ShowPassword.svg"
                            }
                            alt={
                              showPassword ? "Hide password" : "Show password"
                            }
                            className={styles.toggleIcon}
                          />
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <div className={styles.buttonContainer}>
                  <button
                    type="submit"
                    className={styles.loginButton}
                    disabled={loading}
                  >
                    {loading
                      ? isSignup
                        ? "🔄 Creating Account..."
                        : "🔄 Signing In..."
                      : isSignup
                        ? "🚀 Create Account"
                        : "Login"}
                  </button>

                  <button
                    type="button"
                    className={styles.switchMode}
                    onClick={switchMode}
                  >
                    {isSignup ? (
                      "🔄 Already have an account? Sign In"
                    ) : (
                      <>
                        <span className={styles.switchModeMainText}>
                          Don&apos;t have an account? Join Us
                        </span>
                        <span className={styles.switchModeSubtext}>
                          For New Brands
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </form>

              {(error || backendError) && (
                <div className={styles.error}>
                  <div>{error || backendError}</div>
                  {/* Anything the server flagged that has no input of its own
                      still needs to be shown, so nothing is silently dropped. */}
                  {signupIssues.filter((issue) => !fieldErrors[issue.field]).length > 0 && (
                    <ul className={styles.errorList}>
                      {signupIssues
                        .filter((issue) => !fieldErrors[issue.field])
                        .map((issue, index) => (
                          <li key={`${issue.field}-${index}`}>
                            {issue.label ? <strong>{issue.label}: </strong> : null}
                            {issue.message}
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              )}

              {!isSignup && (
                <p className={styles.forgotPassword}>
                  <button
                    type="button"
                    className={styles.link}
                    onClick={handleOpenForgotPassword}
                  >
                    Forgot Password?
                  </button>
                </p>
              )}
            </div>

            <div
              className={`${styles.featuresPanel} ${isSignup ? styles.signupFeaturesPanel : ""}`}
            >
              <div
                className={`${styles.featureGrid} ${isSignup ? styles.signupFeatureGrid : ""}`}
              >
                <img
                  src="images/login-page-banner.svg"
                  alt="Suspire Platform Features"
                  className={`${styles.bannerImage} ${isSignup ? styles.signupBannerImage : ""}`}
                />
              </div>
            </div>
          </main>
        </div>
      </section>

      <section className={styles.partnersSection}>
        <div className={styles.pageWrapper}>
          <div className={styles.partnersCarousel}>
            <div className={styles.partnersTrack} ref={trackRef}>
              {carouselLogos.map((logo, index) => (
                <img
                  key={`${logo.alt}-${index}`}
                  src={logo.src}
                  alt={logo.alt}
                  className={styles.partnerLogo}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      {isForgotPasswordOpen && (
        <ForgotPasswordModal
          initialEmail={username || email}
          onClose={() => setIsForgotPasswordOpen(false)}
        />
      )}

      {isTermsModalOpen &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              zIndex: 99999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1rem",
              backdropFilter: "blur(5px)",
              opacity: 1,
              visibility: "visible",
            }}
            onClick={() => {
              console.log("LoginPage: Overlay clicked, closing modal");
              setIsTermsModalOpen(false);
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "20px",
                maxWidth: "720px",
                width: "95%",
                maxHeight: "90vh",
                position: "relative",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                zIndex: 100000,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.termsModalHeader}>
                <h2 id="terms-modal-title" className={styles.termsModalTitle}>
                  Suspire Brand Application – Terms
                </h2>
                <button
                  className={styles.termsModalClose}
                  onClick={() => setIsTermsModalOpen(false)}
                  aria-label="Close terms and conditions"
                >
                  ×
                </button>
              </div>
              <div className={styles.termsModalBody}>
                <TermsContent />
              </div>
              <div className={styles.termsModalFooter}>
                <button
                  type="button"
                  className={styles.termsModalCloseButton}
                  onClick={() => setIsTermsModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default LoginPage;
